import { heroSideCards } from "@/mocks/homeData";

type HeroSectionProps = {
  onAction: (message: string) => void;
};

const toneStyles: Record<string, { iconWrap: string; chip: string }> = {
  primary: { iconWrap: "bg-primary-500 text-background-50", chip: "bg-primary-100 text-primary-700" },
  accent: { iconWrap: "bg-accent-500 text-background-50", chip: "bg-accent-100 text-accent-800" },
  secondary: { iconWrap: "bg-secondary-500 text-background-50", chip: "bg-secondary-100 text-secondary-900" },
};

export default function HeroSection({ onAction }: HeroSectionProps) {
  return (
    <section id="top" className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20flat%20lay%20of%20fresh%20watermelon%20slices%20arranged%20beside%20stacked%20cream%20paper%20archive%20folders%20and%20index%20cards%20on%20a%20warm%20off%20white%20surface%2C%20soft%20diffused%20daylight%2C%20editorial%20photography%2C%20muted%20amber%20and%20deep%20teal%20accents%2C%20generous%20negative%20space%2C%20clean%20minimal%20composition%2C%20high%20detail&width=1800&height=1100&seq=gcg-hero-bg-2026&orientation=landscape"
          alt="超碰吃瓜时间线档案工作台"
          title="超碰吃瓜时间线完整版"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground-950/85 via-foreground-950/70 to-foreground-950/80"></div>
      </div>

      <div className="relative mx-auto grid w-full max-w-[1400px] gap-10 px-4 py-16 md:px-8 md:py-24 lg:grid-cols-12 lg:items-center">
        <div className="animate-fade-up lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 font-label text-xs tracking-[0.16em] text-background-100 backdrop-blur-sm">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-history-line text-sm"></i>
            </span>
            课代表式时间线整理 · 每日更新
          </span>

          <h2 className="mt-6 font-heading text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
            超碰吃瓜时间线
            <span className="mt-2 block text-primary-300">完整版 · 全记录</span>
          </h2>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            从起点到爆发节点，把零散的信息重新排成一条完整脉络。不想漏掉任何细节，
            就顺着这条时间线，把来龙去脉与前因后果一次看清。
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#timeline"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-timeline-view text-base"></i>
              </span>
              查看时间线
            </a>
            <button
              type="button"
              onClick={() => onAction("完整版全记录下载已开始（演示）")}
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-download-2-line text-base"></i>
              </span>
              下载全记录
            </button>
          </div>

          <p className="mt-6 font-label text-xs tracking-[0.14em] text-background-100/70">
            已归档 1,286 起事件 · 17,904 条节点 · 每日持续更新
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-6">
          {heroSideCards.map((card, index) => {
            const tone = toneStyles[card.tone] ?? toneStyles.primary;
            const isAction = card.kind === "action";
            const cardClass = `animate-fade-up rounded-lg border border-background-200/80 bg-background-50 p-5 text-left ${
              isAction ? "cursor-pointer transition-transform hover:-translate-y-1" : ""
            }`;
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-label text-[11px] tracking-[0.16em] text-foreground-500">
                    {card.label}
                  </span>
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${tone.iconWrap}`}>
                    <i className={`${card.icon} text-base`}></i>
                  </span>
                </div>

                <p className="mt-4 text-sm font-medium leading-snug text-foreground-900">{card.title}</p>

                {card.kind === "progress" ? (
                  <div className="mt-4">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-accent-500"
                        style={{ width: `${card.percent ?? 0}%` }}
                      ></div>
                    </div>
                    <p className="mt-2 font-label text-lg text-accent-700">{card.meta}</p>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-foreground-600">{card.meta}</span>
                    {isAction && (
                      <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${tone.chip}`}>前往下载</span>
                    )}
                  </div>
                )}
              </>
            );

            if (isAction) {
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => onAction("完整版全记录下载已开始（演示）")}
                  className={`w-full ${cardClass}`}
                  style={{ animationDelay: `${120 + index * 90}ms` }}
                >
                  {inner}
                </button>
              );
            }

            return (
              <div
                key={card.id}
                className={cardClass}
                style={{ animationDelay: `${120 + index * 90}ms` }}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}